import React, { useMemo, useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useSelector } from "react-redux";
import { hasPermission } from "utils/permissionHelper";
import { PERMISSIONS } from "constants/permissionTypes";
import { MODULES } from "constants/modulesTypes";
import { useNavigate } from "react-router-dom";

//import components
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/DataTableContainer4";
import { Nav, NavItem, NavLink } from "reactstrap";

const DatatableTables = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortingBy, setSortingBy] = useState("Id");
  const [direction, setDirection] = useState("Desc");
  const [search, setSearch] = useState("");
  const [selectedTab, setSelectedTab] = useState("payments");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isBulkRenewing, setIsBulkRenewing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({
    current: 0,
    total: 0,
  });
  const token = localStorage.getItem('authToken');
  const user = JSON.parse(localStorage.getItem("authUser"));

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        search,
      }).toString();

      const res = await fetch(`https://api.kunalsdrumclass.com/api/GetAllStudentPaymentTransactions?${query}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;
      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );
      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading payments:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, search]);

  const fetchRenewData = useCallback(async () => {
    try {
      setLoading(true);
      const todayDate = moment().format("YYYY-MM-DD");
      const query = new URLSearchParams({
        page,
        countPerPage,
        sortingBy,
        direction,
        statusFilter: selectedTab,
        search,
      }).toString();

      let apiUrl = `https://api.kunalsdrumclass.com/api/GetAllStudents?date=${todayDate}&${query}`;

      const res = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();
      const list = result?.recordsets?.[0] || [];
      const total = result?.recordsets?.[1]?.[0]?.TotalRows;

      if (list.length === 0 && page > 1) {
        setPage(1);
        return;
      }

      setTableData(
        list.map((item, index) => ({
          ...item,
          index: (page - 1) * countPerPage + index + 1,
        }))
      );

      setTotalRecords(total);
    } catch (error) {
      console.error("Error loading students:", error);
    } finally {
      setLoading(false);
    }
  }, [page, countPerPage, sortingBy, direction, selectedTab, search, token]);

  useEffect(() => {
    if (selectedTab === "renewals") {
      fetchRenewData();
      return;
    }

    else {
      fetchData();
    }
  }, [fetchData, fetchRenewData, selectedTab]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setCountPerPage(size);
    setPage(1);
  };

  const handleSortChange = (columnId, desc) => {
    if (!columnId) {
      setSortingBy(null);
      setDirection("Asc");
    } else {
      setSortingBy(columnId);
      setDirection(desc ? "Desc" : "Asc");
    }
    setPage(1);
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleTabChange = (tab) => {
    if (isBulkRenewing) return;

    setTableData([]);
    setTotalRecords(0);
    setSelectedStudents([]);
    setSelectedTab(tab);
    setPage(1);
  };

  const handleSelectStudent = (studentId) => {
    if (isBulkRenewing) return;

    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (isBulkRenewing) return;

    const currentPageIds = tableData.map((row) => row.Id);

    const allSelected = currentPageIds.every((id) =>
      selectedStudents.includes(id)
    );

    if (allSelected) {
      setSelectedStudents((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      setSelectedStudents((prev) => [
        ...new Set([...prev, ...currentPageIds]),
      ]);
    }
  };

  const isAllSelected =
    tableData.length > 0 &&
    tableData.every((row) => selectedStudents.includes(row.Id));

  const renewStudent = async (rowData) => {
    const totalAmount = parseFloat(rowData.Latest_Total_Amount || 0);
    const paidAmount = parseFloat(rowData.Latest_Paid_Amount || 0);

    if (totalAmount !== paidAmount) {
      return {
        success: false,
        paymentPending: true,
      };
    }

    const renewRes = await fetch(
      `https://api.kunalsdrumclass.com/api/RenewStudentById?id=${rowData.Id}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!renewRes.ok) {
      throw new Error("RenewStudentById failed");
    }

    const today = new Date();

    const startDateObj = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    const endDateObj = new Date(
      today.getFullYear(),
      today.getMonth() + Number(rowData.Class_Duration),
      0
    );

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const calculatedStartDate = formatDate(startDateObj);
    const calculatedEndDate = formatDate(endDateObj);

    const payload2 = {
      ids: 0,
      studentId: rowData.Id,
      classDuration: rowData.Class_Duration,
      lessonPlan: rowData.Lesson_Plan,
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      gracePeriod: rowData.Grace_Period_Days,
      createdBy: user.Id,
    };

    const res2 = await fetch(
      `https://api.kunalsdrumclass.com/api/InsertStudentClass`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload2),
      }
    );

    if (!res2.ok) {
      throw new Error("InsertStudentClass failed");
    }

    const res2Data = await res2.json();

    const studentClassId =
      res2Data?.recordsets?.[0]?.[0]?.Id;

    if (!studentClassId) {
      throw new Error("Student class was not created");
    }


    if (rowData.Lesson_Plan === "Twice a week") {
      const days = (rowData.Day_Of_Week || "").split(",");
      const times = (rowData.Time || "").split(",");

      for (let i = 0; i < days.length; i++) {
        const payload = {
          ids: 0,
          studentClassId: studentClassId,
          dayOfWeek: days[i].trim(),
          time: times[i] ? times[i].trim() : "",
          startDate: calculatedStartDate,
          endDate: calculatedEndDate,
          createdBy: user.Id,
        };

        const scheduleRes = await fetch(
          `https://api.kunalsdrumclass.com/api/InsertStudentClassSchedule`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        if (!scheduleRes.ok) {
          throw new Error("Schedule creation failed");
        }
      }
    } else {
      const payload3 = {
        ids: 0,
        studentClassId: studentClassId,
        dayOfWeek: rowData.Day_Of_Week,
        time: rowData.Time,
        startDate: calculatedStartDate,
        endDate: calculatedEndDate,
        createdBy: user.Id,
      };

      const scheduleRes = await fetch(
        `https://api.kunalsdrumclass.com/api/InsertStudentClassSchedule`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload3),
        }
      );

      if (!scheduleRes.ok) {
        throw new Error("Schedule creation failed");
      }
    }


    const oldEnd = new Date(rowData.End_Date);

    const graceEnd = new Date(oldEnd);
    graceEnd.setDate(graceEnd.getDate() + 15);

    const graceEndDate = graceEnd
      .toISOString()
      .split("T")[0];

    const payload6 = {
      oldClassId: rowData.Class_Id,
      newClassId: studentClassId,
      studentId: rowData.Id,
      oldEndDate: rowData.End_Date,
      graceEndDate: graceEndDate,
    };

    const updateRes = await fetch(
      `https://api.kunalsdrumclass.com/api/UpdateRenewStudentClassDetails`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload6),
      }
    );

    if (!updateRes.ok) {
      throw new Error("UpdateRenewStudentClassDetails failed");
    }

    return {
      success: true,
      paymentPending: false,
    };
  };

  const handleRenew = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: "Are you sure you want to renew the student?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      const result = await renewStudent(rowData);

      if (result.paymentPending) {
        await Swal.fire({
          title: "Payment Pending!",
          text: "Student payment is pending. Please collect full payment before renewal.",
          icon: "error",
          confirmButtonColor: "#d32f2f",
          customClass: {
            popup: "swal2-material-ui",
            confirmButton: "error-alert",
          },
        });

        return;
      }

      await Swal.fire({
        title: "Success!",
        text: "Student renewed successfully!",
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      fetchRenewData();
    } catch (error) {
      console.error(error);

      await Swal.fire({
        title: "Error!",
        text: "Something went wrong while renewing the student.",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const showBulkRenewProgress = ({
    completed,
    total,
    studentName = "",
    statusText = "Processing renewal...",
  }) => {
    const percentage =
      total > 0
        ? Math.round((completed / total) * 100)
        : 0;

    if (!Swal.isVisible()) {
      Swal.fire({
        title: "Processing Renewals",
        html: `
        <div class="text-center">

          <div
            id="bulk-renew-progress-text"
            style="
              font-size: 18px;
              margin-bottom: 18px;
              color: #495057;
            "
          >
            <strong>${completed}</strong> out of
            <strong>${total}</strong> done
          </div>

          <div
            style="
              width: 100%;
              height: 12px;
              background: #e9ecef;
              border-radius: 10px;
              overflow: hidden;
              margin-bottom: 18px;
            "
          >
            <div
              id="bulk-renew-progress-bar"
              style="
                width: ${percentage}%;
                height: 100%;
                background: #556ee6;
                transition: width 0.3s ease;
              "
            ></div>
          </div>

          <div
            id="bulk-renew-current-student"
            style="
              font-size: 14px;
              color: #495057;
              margin-bottom: 8px;
            "
          >
            ${studentName
            ? `Student: <strong>${studentName}</strong>`
            : ""
          }
          </div>

          <div
            id="bulk-renew-status"
            style="
              font-size: 13px;
              color: #74788d;
            "
          >
            ${statusText}
          </div>

        </div>
      `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: {
          popup: "swal2-material-ui",
        },
      });

      return;
    }

    const progressText = Swal.getPopup()?.querySelector(
      "#bulk-renew-progress-text"
    );

    const progressBar = Swal.getPopup()?.querySelector(
      "#bulk-renew-progress-bar"
    );

    const currentStudent = Swal.getPopup()?.querySelector(
      "#bulk-renew-current-student"
    );

    const status = Swal.getPopup()?.querySelector(
      "#bulk-renew-status"
    );

    if (progressText) {
      progressText.innerHTML = `
      <strong>${completed}</strong> out of
      <strong>${total}</strong> done
    `;
    }

    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }

    if (currentStudent) {
      currentStudent.innerHTML = studentName
        ? `Student: <strong>${studentName}</strong>`
        : "";
    }

    if (status) {
      status.innerHTML = statusText;
    }
  };

  const handleBulkRenew = async () => {
    if (selectedStudents.length === 0) {
      await Swal.fire({
        title: "No Students Selected",
        text: "Please select at least one student to renew.",
        icon: "info",
        confirmButtonColor: "#3085d6",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });

      return;
    }

    const selectedRows = tableData.filter((row) =>
      selectedStudents.includes(row.Id)
    );

    // ---------------------------------------------
    // Confirmation
    // ---------------------------------------------

    const confirmResult = await Swal.fire({
      title: "Renew Selected Students?",
      text: `You are about to renew ${selectedRows.length} student(s), one by one.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Renew All",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#198754",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "btn btn-sm rounded-8 btn-success",
        cancelButton: "btn btn-sm rounded-8 btn-secondary",
      },
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    const totalStudents = selectedRows.length;

    let renewedCount = 0;
    let skippedCount = 0;
    let failedCount = 0;

    const failedStudents = [];

    setIsBulkRenewing(true);

    // Don't show the table's normal loader.
    // The SweetAlert popup is now our loader/progress UI.
    setLoading(false);

    // ---------------------------------------------
    // Open Progress Popup
    // ---------------------------------------------

    showBulkRenewProgress({
      completed: 0,
      total: totalStudents,
      studentName: "",
      statusText: "Starting renewal process...",
    });

    // Give SweetAlert time to render
    await new Promise((resolve) =>
      setTimeout(resolve, 100)
    );

    try {
      // ---------------------------------------------
      // Process students one by one
      // ---------------------------------------------

      for (let i = 0; i < selectedRows.length; i++) {
        const rowData = selectedRows[i];

        const studentName =
          rowData.Student_Name ||
          rowData.Name ||
          `Student #${rowData.Id}`;

        // -------------------------------------------
        // Show current student
        // -------------------------------------------

        showBulkRenewProgress({
          completed: i,
          total: totalStudents,
          studentName,
          statusText: `Processing renewal ${i + 1}...`,
        });

        try {
          // -----------------------------------------
          // Payment Check
          // -----------------------------------------

          const totalAmount = parseFloat(
            rowData.Latest_Total_Amount || 0
          );

          const paidAmount = parseFloat(
            rowData.Latest_Paid_Amount || 0
          );

          if (totalAmount !== paidAmount) {
            // Close progress popup temporarily
            Swal.close();

            const pendingResult = await Swal.fire({
              title: "Payment Pending",
              html: `
              <div>
                <p>
                  <strong>${studentName}</strong>
                  has pending payment.
                </p>

                <p class="mt-2">
                  Progress:
                  <strong>
                    ${i + 1} / ${totalStudents}
                  </strong>
                </p>
              </div>
            `,
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Skip & Continue",
              cancelButtonText: "Stop",
              confirmButtonColor: "#198754",
              cancelButtonColor: "#dc3545",
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                popup: "swal2-material-ui",
                confirmButton:
                  "btn btn-sm rounded-8 btn-success",
                cancelButton:
                  "btn btn-sm rounded-8 btn-danger",
              },
            });

            if (!pendingResult.isConfirmed) {
              // User selected Stop
              break;
            }

            skippedCount++;

            failedStudents.push({
              studentName,
              reason: "Payment pending",
            });

            // ---------------------------------------
            // Re-open progress popup
            // ---------------------------------------

            showBulkRenewProgress({
              completed: i + 1,
              total: totalStudents,
              studentName,
              statusText:
                "Payment pending. Skipped and continuing...",
            });

            await new Promise((resolve) =>
              setTimeout(resolve, 300)
            );

            continue;
          }

          // -----------------------------------------
          // Renew Student
          // -----------------------------------------

          const renewResult = await renewStudent(rowData);

          if (renewResult.success) {
            renewedCount++;
          }

          // -----------------------------------------
          // Update progress
          // -----------------------------------------

          showBulkRenewProgress({
            completed: i + 1,
            total: totalStudents,
            studentName,
            statusText:
              "Renewal completed. Continuing...",
          });

          await new Promise((resolve) =>
            setTimeout(resolve, 300)
          );

        } catch (error) {
          console.error(
            `Error renewing student ${rowData.Id}:`,
            error
          );

          failedCount++;

          failedStudents.push({
            studentName,
            reason: "Renewal API error",
          });

          // -----------------------------------------
          // Close progress popup
          // -----------------------------------------

          Swal.close();

          const errorResult = await Swal.fire({
            title: "Renewal Failed",
            html: `
            <div>
              <p>
                Renewal failed for
                <strong>${studentName}</strong>.
              </p>

              <p class="mt-2">
                Progress:
                <strong>
                  ${i + 1} / ${totalStudents}
                </strong>
              </p>
            </div>
          `,
            icon: "error",
            showCancelButton: true,
            confirmButtonText: "Skip & Continue",
            cancelButtonText: "Stop",
            confirmButtonColor: "#198754",
            cancelButtonColor: "#dc3545",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              popup: "swal2-material-ui",
              confirmButton:
                "btn btn-sm rounded-8 btn-success",
              cancelButton:
                "btn btn-sm rounded-8 btn-danger",
            },
          });

          if (!errorResult.isConfirmed) {
            break;
          }

          // -----------------------------------------
          // Re-open progress popup
          // -----------------------------------------

          showBulkRenewProgress({
            completed: i + 1,
            total: totalStudents,
            studentName,
            statusText:
              "Renewal failed. Skipped and continuing...",
          });

          await new Promise((resolve) =>
            setTimeout(resolve, 300)
          );
        }
      }

      // ---------------------------------------------
      // Close Progress Popup
      // ---------------------------------------------

      Swal.close();

      // ---------------------------------------------
      // Final Result
      // ---------------------------------------------

      let failedHtml = "";

      if (failedStudents.length > 0) {
        failedHtml = `
        <div class="mt-3 text-start">
          <strong>Skipped / Failed:</strong>

          <ul>
            ${failedStudents
            .map(
              (item) => `
                  <li>
                    ${item.studentName}
                    - ${item.reason}
                  </li>
                `
            )
            .join("")}
          </ul>
        </div>
      `;
      }

      await Swal.fire({
        title: "Renewal Completed",
        html: `
        <div class="text-start">

          <p>
            <strong>Renewed:</strong>
            ${renewedCount}
          </p>

          <p>
            <strong>Skipped:</strong>
            ${skippedCount}
          </p>

          <p>
            <strong>Failed:</strong>
            ${failedCount}
          </p>

          ${failedHtml}

        </div>
      `,
        icon:
          skippedCount > 0 || failedCount > 0
            ? "warning"
            : "success",
        confirmButtonColor: "#198754",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton:
            "btn btn-sm rounded-8 btn-primary",
        },
      });

      // ---------------------------------------------
      // Clear selection and refresh
      // ---------------------------------------------

      setSelectedStudents([]);

      await fetchRenewData();

    } catch (error) {
      console.error(
        "Bulk renewal error:",
        error
      );

      Swal.close();

      await Swal.fire({
        title: "Bulk Renewal Error",
        text:
          "Something went wrong while processing the selected students.",
        icon: "error",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton:
            "btn btn-sm rounded-8 btn-danger",
        },
      });

    } finally {
      setIsBulkRenewing(false);

      setBulkProgress({
        current: 0,
        total: 0,
      });
    }
  };

  const sortingState = useMemo(() => {
    if (!sortingBy) return [];
    return [{ id: sortingBy, desc: direction === "Desc" }];
  }, [sortingBy, direction]);

  const permissionMap = useSelector(
    state => state.permissions?.permissions || {}
  );

  const columns = useMemo(() => {
    if (selectedTab === "payments") {
      return [
        {
          header: "Id",
          accessorKey: "Id",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Name",
          meta: {
            sticky: true,
            width: "125px",
          },
          accessorKey: "Student_Name",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Contact No",
          accessorKey: "Contact_Number",
          enableSorting: true,
          enableColumnFilter: false,

          cell: ({ row }) => {
            const rowData = row.original;

            const mobile = rowData.Contact_Number || "";
            const cleanNumber = mobile.replace(/\D/g, "");
            const email = rowData.Email_Address || "";

            return (
              <div className="d-flex flex-column gap-1">
                <span>{mobile}</span>

                <div className="d-flex gap-2 align-items-center">
                  <a
                    href={`tel:+${cleanNumber}`}
                    title="Call"
                  >
                    <i className="mdi mdi-phone-outline fs-3" />
                  </a>

                  <a
                    href={`https://wa.me/${cleanNumber}`}
                    target="_blank"
                    rel="noreferrer"
                    title="WhatsApp"
                  >
                    <i className="mdi mdi-whatsapp fs-3" />
                  </a>

                  <a
                    href={
                      /Mobi|Android/i.test(navigator.userAgent)
                        ? `mailto:${email}`
                        : `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                          email
                        )}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Send Email"
                  >
                    <i className="mdi mdi-email-send-outline fs-3" />
                  </a>
                </div>
              </div>
            );
          },
        },
        {
          header: "Payment Month",
          accessorKey: "Payment_Month",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Payment Date",
          accessorKey: "Payment_Date",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) =>
            info.getValue()
              ? moment(info.getValue()).format("DD-MM-YYYY")
              : "",
        },
        {
          header: "Amount",
          accessorKey: "Amount_Paid",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Payment Mode",
          accessorKey: "Payment_Mode",
          enableSorting: true,
          enableColumnFilter: false,
        },
        {
          header: "Created Date",
          accessorKey: "CreatedOn",
          enableSorting: true,
          enableColumnFilter: false,
          cell: (info) =>
            info.getValue()
              ? moment(info.getValue()).format("DD-MM-YYYY")
              : "",
        },
        {
          header: "Status",
          accessorKey: "IsFeesPending",
          enableSorting: true,
          enableColumnFilter: false,

          cell: ({ row }) => {
            const isFeesPending =
              row.original.IsFeesPending === 1;

            return (
              <div className="d-flex gap-2">
                <span
                  className={`badge ${isFeesPending
                    ? "bg-danger"
                    : "bg-success"
                    }`}
                >
                  {isFeesPending
                    ? "Physical Fees Pending"
                    : "Fees Paid"}
                </span>
              </div>
            );
          },
        },
        {
          header: "Action",
          enableSorting: false,
          enableColumnFilter: false,

          cell: ({ row }) => {
            const rowData = row.original;

            return (
              <div className="d-flex gap-2">
                {hasPermission(
                  permissionMap,
                  MODULES.PAYMENTS,
                  PERMISSIONS.VIEW
                ) && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        navigate(`/payment/view/${rowData.Id}`)
                      }
                    >
                      View
                    </button>
                  )}

                {hasPermission(
                  permissionMap,
                  MODULES.PAYMENTS,
                  PERMISSIONS.EDIT
                ) && (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() =>
                        navigate(`/payment/edit/${rowData.Id}`)
                      }
                    >
                      Edit
                    </button>
                  )}

                {hasPermission(
                  permissionMap,
                  MODULES.PAYMENTS,
                  PERMISSIONS.DELETE
                ) && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(rowData)}
                    >
                      Delete
                    </button>
                  )}
              </div>
            );
          },
        },
      ];
    }

    return [
      {
        id: "select",
        header: () => (
          <div className="text-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={isAllSelected}
              onChange={handleSelectAll}
              disabled={isBulkRenewing}
            />
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <div className="text-center">
              <input
                type="checkbox"
                className="form-check-input"
                checked={selectedStudents.includes(rowData.Id)}
                onChange={() =>
                  handleSelectStudent(rowData.Id)
                }
                disabled={isBulkRenewing}
              />
            </div>
          );
        },
      },
      {
        header: "Id",
        accessorKey: "Id",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Name",
        meta: {
          sticky: true,
          width: "125px",
        },
        accessorKey: "Student_Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Parent Name",
        accessorKey: "Parent_Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Contact No",
        accessorKey: "Contact_Number",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ row }) => {
          const rowData = row.original;

          const mobile = rowData.Contact_Number || "";
          const cleanNumber = mobile.replace(/\D/g, "");
          const email = rowData.Email_Address || "";

          return (
            <div className="d-flex flex-column gap-1">
              <span>{mobile}</span>

              <div className="d-flex gap-2 align-items-center">
                <a
                  href={`tel:+${cleanNumber}`}
                  title="Call"
                >
                  <i className="mdi mdi-phone-outline fs-3" />
                </a>

                <a
                  href={`https://wa.me/${cleanNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  title="WhatsApp"
                >
                  <i className="mdi mdi-whatsapp fs-3" />
                </a>

                <a
                  href={
                    /Mobi|Android/i.test(navigator.userAgent)
                      ? `mailto:${email}`
                      : `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                        email
                      )}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Send Email"
                >
                  <i className="mdi mdi-email-send-outline fs-3" />
                </a>
              </div>
            </div>
          );
        },
      },
      {
        header: "Email",
        accessorKey: "Email_Address",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: "Duration",
        accessorKey: "Class_Duration",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ getValue }) => {
          const value = getValue();

          switch (value) {
            case 1:
              return "Monthly";

            case 3:
              return "Quarterly";

            default:
              return "N/A";
          }
        },
      },
      {
        header: "Class Day",
        accessorKey: "Day_Of_Week",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ getValue }) =>
          getValue() || "N/A",
      },
      {
        header: "Class Time",
        accessorKey: "Time",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ getValue }) =>
          getValue() || "N/A",
      },
      {
        header: "Created Date",
        accessorKey: "CreatedOn",
        enableSorting: true,
        enableColumnFilter: false,

        cell: (info) =>
          info.getValue()
            ? moment(info.getValue()).format("DD-MM-YYYY")
            : "",
      },
      {
        header: "Status",
        accessorKey: "IsActive",
        enableSorting: true,
        enableColumnFilter: false,

        cell: ({ row }) => {
          const isActive =
            row.original.IsActive === 1;

          const isApproved =
            row.original.IsApproved === 1;

          return (
            <div className="d-flex gap-2">
              <span
                className={`badge ${isActive
                  ? "bg-success"
                  : "bg-danger"
                  }`}
              >
                {isActive ? "Active" : "Inactive"}
              </span>

              <span
                className={`badge ${isApproved
                  ? "bg-success"
                  : "bg-danger"
                  }`}
              >
                {isApproved
                  ? "Approved"
                  : "Rejected"}
              </span>
            </div>
          );
        },
      },
      {
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,

        cell: ({ row }) => {
          const rowData = row.original;

          return (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex gap-2">
                {hasPermission(
                  permissionMap,
                  MODULES.PAYMENTS,
                  PERMISSIONS.EDIT
                ) && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleRenew(rowData)}
                    >
                      Renew
                    </button>
                  )}
              </div>
            </div>
          );
        },
      },
    ];
  }, [
    selectedTab,
    permissionMap,
    selectedStudents,
    isBulkRenewing,
    isAllSelected,
  ]);

  const handleDelete = async (rowData) => {
    const result = await Swal.fire({
      title: "Warning!",
      text: `Are you sure you want to delete this payment?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, continue",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
      customClass: {
        popup: "swal2-material-ui",
        confirmButton: "delete-alert",
        cancelButton: "cancel-alert",
      },
    });

    if (!result.isConfirmed) return;
    try {
      const payload = { id: rowData.Id, modifiedBy: user.Id };
      await fetch(`https://api.kunalsdrumclass.com/api/DeleteStudentPaymentById?id=${payload.id}&modifiedBy=${payload.modifiedBy}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      await Swal.fire({
        title: "Success!",
        text: `payment deleted successfully!`,
        icon: "success",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "btn btn-sm rounded-8 btn-primary",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        title: "Error!",
        text: "Something went wrong!",
        icon: "error",
        confirmButtonColor: "#d32f2f",
        customClass: {
          popup: "swal2-material-ui",
          confirmButton: "error-alert",
        },
      });
    }
  };

  //meta title
  document.title = "Payments | KDC - Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Payments"
          breadcrumbItems={[
            { title: "Payments" },
          ]}
        />

        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <div className="d-flex gap-2 mb-2 flex-wrap">
            <Nav tabs>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedTab === "payments",
                  })}
                  onClick={() => handleTabChange("payments")}
                >
                  Payments
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({
                    active: selectedTab === "renewals",
                  })}
                  onClick={() => handleTabChange("renewals")}
                >
                  Renewals
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          <div className="mb-3 text-end d-flex gap-2 justify-content-end flex-wrap">

            {selectedTab === "renewals" && (
              <button
                className="btn btn-success mb-2"
                onClick={handleBulkRenew}
                disabled={
                  selectedStudents.length === 0 ||
                  isBulkRenewing
                }
              >
                {isBulkRenewing ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />

                    Renewing{" "}
                    {bulkProgress.current} /{" "}
                    {bulkProgress.total}
                  </>
                ) : (
                  <>
                    Renew Selected
                    {selectedStudents.length > 0 &&
                      ` (${selectedStudents.length})`}
                  </>
                )}
              </button>
            )}

            <button
              className="btn btn-primary mb-2"
              onClick={() => navigate("/payment/create")}
              disabled={isBulkRenewing}
            >
              + Create Payment
            </button>

          </div>
        </div>

        <TableContainer
          columns={columns}
          data={tableData}
          page={page}
          countPerPage={countPerPage}
          totalRecords={totalRecords}
          sortingState={sortingState}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          searchValue={search}
          onSearch={handleSearch}
          isLoading={loading}
          isPagination
          isGlobalFilter
          isCustomPageSize
          SearchPlaceholder="search..."
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

      </div>
    </div>
  );
};

export default DatatableTables;
